import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { DELIVERY_BOARD_FEED_PATH } from './useDeliveryBoardFeed';
import { DELIVERY_BOARD_PATH } from './useDeliveryBoardData';
import { invalidateOrder } from './invalidateOrder';

export interface ReceivePartsInput {
    orderPartIds: string[];
    /** true = mark received, false = undo */
    received: boolean;
}

interface ReceivePartsResponse {
    updatedCount: number;
    orderIds: string[];
}

const receiveParts = async (
    sessionId: string,
    input: ReceivePartsInput
): Promise<ReceivePartsResponse> => {
    const { data } = await axios.post(`${API_URL}/delivery/receive`, input, {
        headers: {
            Authorization: sessionId
        }
    });
    return data;
};

interface UseReceivePartsOptions {
    /**
     * Invalidate the Delivery Board queries on success (default true) so a
     * receive fired from any other page (OrderedTabs, order drawer) doesn't
     * leave the board's cached feed serving stale rows within its staleTime.
     *
     * The Delivery Board surfaces themselves opt out: they keep just-received
     * rows visible locally (struck through, undoable), and an invalidation
     * would refetch those rows away and drop the undo affordance.
     */
    invalidateBoard?: boolean;
}

/** Mark parts received (or undo). */
export const useReceiveParts = (options?: UseReceivePartsOptions) => {
    const invalidateBoard = options?.invalidateBoard ?? true;
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: ReceivePartsInput) =>
            receiveParts(sessionContext?.sessionId ?? '', input),
        onSuccess: (data) => {
            // Keep the whole order query family in sync (OrderedTabs, order
            // drawer, activity log) — receiving is reachable from the Delivery
            // Board while a drawer is mounted, and the API emits
            // parts-received / parts-unreceived activity entries.
            if (data.orderIds?.length) {
                data.orderIds.forEach((orderId) => {
                    void invalidateOrder(queryClient, orderId);
                });
            } else {
                queryClient.invalidateQueries({ queryKey: ['/order/detail'] });
                queryClient.invalidateQueries({ queryKey: ['/order/activity-logs'] });
                queryClient.invalidateQueries({ queryKey: ['/order/drawer'] });
            }
            if (invalidateBoard) {
                queryClient.invalidateQueries({ queryKey: [DELIVERY_BOARD_FEED_PATH] });
                queryClient.invalidateQueries({ queryKey: [DELIVERY_BOARD_PATH] });
            }
        },
        onError: (error) => {
            console.error('Failed to update received parts:', error);
            // Nothing changed server-side, so this refetch is only a
            // conservative resync for callers away from the board (e.g. a
            // request that committed but errored in transit). It must honor
            // the board's opt-out: force-reloading there would drop every
            // just-received row (and its undo control) off the screen right
            // after the user was told the request failed.
            if (invalidateBoard) {
                queryClient.invalidateQueries({ queryKey: [DELIVERY_BOARD_FEED_PATH] });
                queryClient.invalidateQueries({ queryKey: [DELIVERY_BOARD_PATH] });
            }
        },
    });
};
